import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Users, RefreshCw, Search, Download } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const money = (v) => `$${(Number(v) || 0).toFixed(2)}`;
const fmtDate = (iso) => {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch { return '—'; }
};

/**
 * Flat cross-ambassador referee list — every player across every
 * ambassador with lifetime loaded / commission earned. Backed by
 * GET /api/admin/ambassador-referees. Supports substring search
 * (referee name or ambassador code) and filter chips.
 */
export const AmbassadorRefereesTable = () => {
    const [rows, setRows] = useState([]);
    const [totals, setTotals] = useState({ count: 0, lifetime_loaded: 0, commission_earned: 0 });
    const [loading, setLoading] = useState(false);
    const [q, setQ] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [sortBy, setSortBy] = useState('created');

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ limit: '1000' });
            if (q.trim()) params.set('q', q.trim());
            if (statusFilter !== 'all') params.set('status', statusFilter);
            const { data } = await axios.get(`${API}/admin/ambassador-referees?${params}`);
            setRows(data.rows || []);
            setTotals(data.totals || { count: 0, lifetime_loaded: 0, commission_earned: 0 });
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not load ambassador referees');
        }
        setLoading(false);
    }, [q, statusFilter]);

    useEffect(() => { load(); }, [load]);

    const sorted = useMemo(() => {
        const arr = [...rows];
        switch (sortBy) {
            case 'loaded': arr.sort((a, b) => b.lifetime_loaded - a.lifetime_loaded); break;
            case 'commission': arr.sort((a, b) => b.commission_earned - a.commission_earned); break;
            case 'ambassador': arr.sort((a, b) => (a.ambassador_name || '').localeCompare(b.ambassador_name || '')); break;
            case 'created':
            default: arr.sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
        }
        return arr;
    }, [rows, sortBy]);

    const exportCsv = () => {
        const header = ['Ambassador', 'Code', 'Commission %', 'Referee', 'Phone', 'Signup Status',
            'Lifetime Loaded', 'Cashed Out', 'Net House Profit', 'Commission Earned', 'Signed Up'];
        const escape = (v) => {
            const s = v == null ? '' : String(v);
            return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
        };
        const lines = [header.join(',')];
        sorted.forEach(r => lines.push([
            r.ambassador_name, r.ambassador_code, r.commission_pct,
            r.referee_name, r.referee_phone || '',
            r.signup_status,
            r.lifetime_loaded, r.lifetime_cashed_out, r.net_house_profit,
            r.commission_earned,
            r.created_at ? new Date(r.created_at).toISOString().slice(0, 10) : '',
        ].map(escape).join(',')));
        const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `ambassador-referees-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="card-dark p-5 mt-5" style={{ borderLeft: '3px solid #A855F7' }} data-testid="ambassador-referees-panel">
            <div className="flex items-center gap-2 mb-1">
                <Users className="w-5 h-5 text-[#A855F7]" />
                <h3 className="text-lg font-bold text-white">All Referees — cross-ambassador view</h3>
            </div>
            <p className="text-xs text-[#A0AAB2] mb-4">
                Every player, every ambassador, all in one place. Search by referee or ambassador code.
            </p>

            {/* Summary chips */}
            <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="rounded-lg bg-[#0a0a0a] border border-white/10 p-3">
                    <div className="text-[10px] uppercase tracking-wider text-[#A0AAB2] font-bold">Referees</div>
                    <div className="text-xl font-bold text-white mt-0.5" data-testid="referees-count">{totals.count}</div>
                </div>
                <div className="rounded-lg bg-[#0a0a0a] border border-white/10 p-3">
                    <div className="text-[10px] uppercase tracking-wider text-[#A0AAB2] font-bold">Lifetime loaded</div>
                    <div className="text-xl font-bold text-white mt-0.5">{money(totals.lifetime_loaded)}</div>
                </div>
                <div className="rounded-lg bg-[#0a0a0a] border border-white/10 p-3">
                    <div className="text-[10px] uppercase tracking-wider text-[#A0AAB2] font-bold">Commission earned</div>
                    <div className="text-xl font-bold text-[#FFD700] mt-0.5">{money(totals.commission_earned)}</div>
                </div>
            </div>

            {/* Controls */}
            <div className="flex flex-wrap items-center gap-2 mb-3">
                <div className="relative flex-1 min-w-[200px]">
                    <Search className="w-4 h-4 text-[#A0AAB2] absolute left-2.5 top-1/2 -translate-y-1/2" />
                    <Input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search by referee or code…"
                        className="h-9 pl-8 bg-[#121212] border-white/10 text-white text-sm"
                        data-testid="referees-search"
                    />
                </div>
                <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="h-9 px-3 rounded-md bg-[#121212] border border-white/10 text-white text-sm"
                    data-testid="referees-status-filter"
                >
                    <option value="all">All statuses</option>
                    <option value="active">Active</option>
                    <option value="pending">Pending</option>
                    <option value="qualified">Qualified</option>
                    <option value="cancelled">Cancelled</option>
                </select>
                <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="h-9 px-3 rounded-md bg-[#121212] border border-white/10 text-white text-sm"
                    data-testid="referees-sort"
                >
                    <option value="created">Newest first</option>
                    <option value="loaded">Loaded (high→low)</option>
                    <option value="commission">Commission (high→low)</option>
                    <option value="ambassador">Ambassador A-Z</option>
                </select>
                <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="referees-refresh">
                    <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />Refresh
                </Button>
                <Button variant="outline" size="sm" onClick={exportCsv} disabled={rows.length === 0} data-testid="referees-export">
                    <Download className="w-4 h-4 mr-1" />CSV
                </Button>
            </div>

            {/* Table */}
            <div className="overflow-x-auto rounded-lg border border-white/10">
                <table className="w-full text-sm">
                    <thead className="bg-[#0a0a0a] text-[10px] uppercase tracking-wider text-[#A0AAB2]">
                        <tr>
                            <th className="text-left px-3 py-2">Ambassador</th>
                            <th className="text-left px-3 py-2">Code</th>
                            <th className="text-left px-3 py-2">Referee</th>
                            <th className="text-left px-3 py-2">Phone</th>
                            <th className="text-right px-3 py-2">Loaded</th>
                            <th className="text-right px-3 py-2">Cashed</th>
                            <th className="text-right px-3 py-2">Net profit</th>
                            <th className="text-right px-3 py-2">Commission</th>
                            <th className="text-left px-3 py-2">Signed up</th>
                            <th className="text-left px-3 py-2">Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sorted.length === 0 ? (
                            <tr>
                                <td colSpan={10} className="text-center text-[#A0AAB2] py-6" data-testid="referees-empty">
                                    {loading ? 'Loading…' : 'No referees match.'}
                                </td>
                            </tr>
                        ) : sorted.map((r) => (
                            <tr key={r.signup_id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`referee-row-${r.signup_id}`}>
                                <td className="px-3 py-2 text-white">{r.ambassador_name}</td>
                                <td className="px-3 py-2 text-[#A0AAB2] font-mono text-xs">{r.ambassador_code}</td>
                                <td className="px-3 py-2 text-white">
                                    {r.referee_name}
                                    {r.manually_linked_at && (
                                        <span className="ml-1 text-[9px] uppercase tracking-wider text-[#FF8C00] align-middle" title="Manually linked by admin">
                                            · retro
                                        </span>
                                    )}
                                </td>
                                <td className="px-3 py-2 text-[#A0AAB2] font-mono text-xs">{r.referee_phone || '—'}</td>
                                <td className="px-3 py-2 text-right text-white">{money(r.lifetime_loaded)}</td>
                                <td className="px-3 py-2 text-right text-[#A0AAB2]">{money(r.lifetime_cashed_out)}</td>
                                <td className={`px-3 py-2 text-right ${r.net_house_profit > 0 ? 'text-[#39FF14]' : r.net_house_profit < 0 ? 'text-[#FF6B6B]' : 'text-[#A0AAB2]'}`}>
                                    {money(r.net_house_profit)}
                                </td>
                                <td className="px-3 py-2 text-right text-[#FFD700] font-semibold">{money(r.commission_earned)}</td>
                                <td className="px-3 py-2 text-[#A0AAB2]">{fmtDate(r.created_at)}</td>
                                <td className="px-3 py-2">
                                    <span className={`text-[10px] uppercase tracking-wider font-bold ${
                                        r.signup_status === 'qualified' ? 'text-[#39FF14]' :
                                        r.signup_status === 'active' ? 'text-white' :
                                        r.signup_status === 'cancelled' ? 'text-[#FF6B6B]' : 'text-[#FFD700]'
                                    }`}>{r.signup_status}</span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default AmbassadorRefereesTable;
